/**
 * 商品搜索Mixin
 * 提供統一的搜索狀態管理和交互邏輯
 */

import { createProductSearchService } from '@g/utils/productSearchService'

export default {
  data () {
    return {
      // 搜索相關狀態
      searchKey: '',
      searchLoading: false,
      searchGoodsList: [],
      searchError: '',

      // 選中商品相關
      choseItem: null,
      itemDetailLoading: null, // 當前正在加載詳情的商品ID

      // 搜索服務實例
      searchService: null
    }
  },

  computed: {
    /**
     * 選中商品的ID
     */
    choseItemid () {
      return this.choseItem ? (this.choseItem.id || this.choseItem.itemid) : ''
    },

    /**
     * 是否有搜索結果
     */
    hasSearchResults () {
      return this.searchGoodsList && this.searchGoodsList.length > 0
    },

    /**
     * 是否顯示錯誤信息
     */
    shouldShowError () {
      return this.searchError && !this.searchLoading && !this.hasSearchResults
    }
  },

  created () {
    // 初始化搜索服務
    this.searchService = createProductSearchService(this.$store)
  },

  methods: {
    /**
     * 關鍵詞搜索
     * @param {string} keyword 搜索關鍵詞，為空則使用 searchKey
     * @param {Object} options 搜索選項
     */
    async searchByKeyword (keyword = null, options = {}) {
      const searchKeyword = keyword || this.searchKey

      if (!searchKeyword.trim()) {
        this.searchError = '請輸入搜索關鍵詞'
        return
      }

      try {
        this.searchLoading = true
        this.searchError = ''
        this.searchGoodsList = []
        this.choseItem = null

        // 準備搜索選項
        const searchOptions = {
          pageSize: 12,
          limit: 12,
          filter: this.createSearchFilter(),
          ...options
        }

        // 執行搜索
        const results = await this.searchService.searchByKeyword(
          searchKeyword,
          searchOptions
        )

        this.searchGoodsList = results

        // 觸發搜索完成事件
        this.$emit('search-complete', {
          keyword: searchKeyword,
          results,
          total: results.length
        })

        // 如果沒有結果
        if (results.length === 0) {
          this.searchError = '搜尋不到相符的商品，請試試其他關鍵字'
        }
      } catch (error) {
        console.error('搜索失敗:', error)
        this.searchError = error.message || '搜索失敗，請重試'

        // 觸發搜索錯誤事件
        this.$emit('search-error', {
          keyword: searchKeyword,
          error: error.message
        })
      } finally {
        this.searchLoading = false
      }
    },

    /**
     * 圖片搜索
     * @param {string} imageBase64 圖片base64數據
     * @param {Object} options 搜索選項
     */
    async searchByImage (imageBase64, options = {}) {
      if (!imageBase64) {
        this.searchError = '請選擇圖片'
        return
      }

      try {
        this.searchLoading = true
        this.searchError = ''
        this.searchGoodsList = []
        this.choseItem = null

        // 準備搜索選項
        const searchOptions = {
          limit: 12,
          filter: this.createSearchFilter(),
          ...options
        }

        // 執行圖片搜索
        const results = await this.searchService.searchByImage(
          imageBase64,
          searchOptions
        )

        this.searchGoodsList = results

        // 觸發搜索完成事件
        this.$emit('search-complete', {
          type: 'image',
          results,
          total: results.length
        })

        if (results.length === 0) {
          this.searchError = '未找到相似商品，請嘗試其他圖片或使用關鍵字搜索'
        }
      } catch (error) {
        console.error('圖片搜索失敗:', error)
        this.searchError = error.message || '圖片識別失敗，請重試'

        this.$emit('search-error', {
          type: 'image',
          error: error.message
        })
      } finally {
        this.searchLoading = false
      }
    },

    /**
     * 選擇/取消選擇商品
     * @param {Object} item 商品信息
     */
    async toggleItem (item) {
      const itemid = item.itemid || item.id

      // 如果是取消選擇
      if (this.choseItemid === itemid) {
        this.choseItem = null
        this.$emit('item-changed', null)
        return
      }

      try {
        // 檢查是否需要補全商品詳情
        if (!item.itemCid && item.searchSource === 'tbdg-search') {
          this.itemDetailLoading = itemid

          // 懶加載商品詳情
          item = await this.searchService.fillItemDetail(item)

          // 更新列表中的商品信息
          const index = this.searchGoodsList.findIndex(
            goods => (goods.id || goods.itemid) === itemid
          )
          if (index !== -1) {
            this.$set(this.searchGoodsList, index, item)
          }
        }
        this.choseItem = item
        if (item.itemCid) {
          this.$emit('item-changed', item)
        }
      } catch (error) {
        console.warn('獲取商品詳情失敗:', error)
        // 即使詳情獲取失敗，也允許選擇商品
        this.choseItem = item
        this.$emit('item-changed', item)
      } finally {
        this.itemDetailLoading = null
      }
    },

    /**
     * 重置搜索狀態
     */
    resetSearch () {
      this.searchKey = ''
      this.searchGoodsList = []
      this.searchError = ''
      this.choseItem = null
      this.itemDetailLoading = null
    },

    /**
     * 創建搜索過濾器
     * 子組件可以重寫此方法來自定義過濾邏輯
     */
    createSearchFilter () {
      return (item) => {
        // 默認過濾邏輯：過濾掉沒有圖片的商品
        if (!item.itemPic && (!item.imageUrls || item.imageUrls.length === 0)) {
          return false
        }

        // 子組件可以添加自定義過濾邏輯
        if (this.customFilter && typeof this.customFilter === 'function') {
          return this.customFilter(item)
        }

        return true
      }
    },

    /**
     * 處理輸入框回車事件
     */
    onSearchKeyEnter () {
      this.searchByKeyword()
    },

    /**
     * 處理輸入框清空事件
     */
    onSearchKeyClear () {
      this.resetSearch()
    },

    /**
     * 獲取商品圖片URL
     */
    getItemImage (item) {
      if (item.imageUrls && item.imageUrls.length > 0) {
        return item.imageUrls[0]
      }
      if (item.itemPic) {
        return item.itemPic.replace('_80x80.jpg', '_200x200.jpg')
      }
      return ''
    },

    /**
     * 獲取商品標題
     */
    getItemTitle (item) {
      return item.title || item.itemName || ''
    },

    /**
     * 檢查商品是否正在加載詳情
     */
    isItemDetailLoading (item) {
      const itemid = item.itemid || item.id
      return this.itemDetailLoading === itemid
    },

    /**
     * 檢查商品是否已選中
     */
    isItemSelected (item) {
      const itemid = item.itemid || item.id
      return this.choseItemid === itemid
    }
  }
}
